import { useMemo, type FC } from 'react';

import { EntitlementViewBase, ProductViewBase } from '@amzn/coral_com-amazon-sssapinterface-model-structures-credits';
import { useBundle } from '@amzn/react-arb-tools';
import {
  ComputedInput,
  COMPUTED_INPUT_STATE,
  ComputedInputValue,
  formatCurrency,
  OFFER_TIER,
} from '@amzn/sss-website-theme';

import { CALCULATING_INPUT_LABEL, CHECKOUT_BUNDLE_NAME, COMMON_BUNDLE_NAME } from 'src/constants';
import { calculateBundleQuantity } from 'src/helpers/bundles/bundle-helpers';
import { getMarketplaceTaxInfo } from 'src/helpers/tax/tax-helpers';
import { t } from 'src/helpers/translations/translation-helpers';

import styles from './CreditsComputedInfo.module.scss';

const ADJUSTMENT_FACTOR = 0.1;

interface CreditsComputedInfopProps {
  product: ProductViewBase;
  /** When provided, entitlement is the source of truth for pricing (steps 2 & 3) */
  entitlement?: EntitlementViewBase;
  quantity: number;
  showAllowedAdjustments?: boolean;
  /** Optional delivery date string to display as the first line item (used in steps 2 & 3) */
  deliveryDate?: string;
  /** Marketplace ID for tax calculation */
  marketplaceId: string | undefined;
  /** Whether to show the bundle rounding note. Defaults to true (step 1). Set false for steps 2 & 3. */
  showBundleNote?: boolean;
  /** Checkout step variant controls label text. 'order' (step 1/2) or 'confirmed' (step 3). Defaults to 'order'. */
  variant?: 'order' | 'confirmed';
}

export const CreditsComputedInfo: FC<CreditsComputedInfopProps> = ({
  product,
  entitlement,
  quantity,
  showAllowedAdjustments,
  deliveryDate,
  marketplaceId,
  showBundleNote = true,
  variant = 'order',
}) => {
  const [commonBundle] = useBundle(COMMON_BUNDLE_NAME);
  const [checkoutBundle] = useBundle(CHECKOUT_BUNDLE_NAME);

  // ─── Pricing variables ───────────────────────────────────────────────────────
  // When an entitlement is provided (steps 2 & 3), use its confirmed values.
  // Otherwise (step 1), calculate from the product's current offer prices.

  const { productBundleFactor, productOfferCurrency, productMatchedOfferTier, productBaseOfferPrice } = product;

  const showBundles = entitlement ? !!entitlement.entitlementBundleQuantity : !!productBundleFactor;
  const currencyCode = entitlement?.entitlementCurrencyCode ?? productOfferCurrency;

  // Resolved pricing values: entitlement (source of truth) or product (live preview)
  const unitPrice = entitlement ? entitlement.entitlementUnitPrice : (product.productMatchedOfferPrice ?? 0);
  const creditQuantity = entitlement ? entitlement.entitlementQuantity : quantity;
  const bundleFactor = entitlement?.entitlementBundleFactor ?? productBundleFactor;

  // Pricing tier: use entitlement fields exclusively for steps 2 & 3, product for step 1.
  // No fallback to product data when entitlement is present — a TCP customer may have a BASE-tier
  // entitlement, and falling back to product data could incorrectly surface a discount that doesn't
  // reflect their actual entitlement. The backend guarantees these fields on new entitlements
  // (CR-293023941), and the backfill has stamped them on all existing records.
  // Note: Step 4 does not use CreditsComputedInfo.
  const resolvedPriceTier = entitlement ? entitlement.entitlementPriceTier : productMatchedOfferTier;
  const resolvedBasePrice = entitlement ? entitlement.entitlementBasePrice : productBaseOfferPrice;

  // Credit limit (only relevant for step 1 live preview adjustments)
  const creditLimit = showBundles && bundleFactor ? 1000 * bundleFactor : 1000;
  const adjustedQuantity = Math.min(creditQuantity, creditLimit);

  // Bundle quantity
  const bundleQuantity = useMemo(() => {
    if (!showBundles) return 0;
    if (entitlement?.entitlementBundleQuantity) return entitlement.entitlementBundleQuantity;
    return calculateBundleQuantity(adjustedQuantity, bundleFactor);
  }, [showBundles, entitlement?.entitlementBundleQuantity, adjustedQuantity, bundleFactor]);

  // Tax info from marketplace
  const { type: taxType, rate: taxRate } = getMarketplaceTaxInfo(marketplaceId);

  // TCP signatory pricing: determine if the user has a discounted TCP tier
  const isTcpPricing = useMemo(
    () =>
      resolvedPriceTier != null &&
      resolvedPriceTier !== OFFER_TIER.BASE &&
      resolvedBasePrice != null &&
      unitPrice != null &&
      resolvedBasePrice > unitPrice,
    [resolvedPriceTier, resolvedBasePrice, unitPrice]
  );

  // ─── Computed totals ─────────────────────────────────────────────────────────

  // Credits total: the actual amount charged (uses matched/TCP price)
  const creditsTotal = useMemo(() => {
    if (showBundles && bundleQuantity > 0) {
      // For entitlement: unitPrice IS the bundle price
      // For product: use productMatchedOfferBundlePrice if available, else unitPrice * bundleQuantity
      if (entitlement) {
        return unitPrice * bundleQuantity;
      }
      const bundlePrice = product.productMatchedOfferBundlePrice;
      return bundlePrice !== undefined ? bundlePrice * bundleQuantity : unitPrice * adjustedQuantity;
    }
    return unitPrice * adjustedQuantity;
  }, [showBundles, bundleQuantity, entitlement, unitPrice, product.productMatchedOfferBundlePrice, adjustedQuantity]);

  // Base price total (before TCP discount) for calculating the signatory discount
  const basePriceTotal = useMemo(() => {
    if (!isTcpPricing || resolvedBasePrice == null) return undefined;
    if (showBundles && bundleQuantity > 0) {
      if (entitlement) {
        // Entitlement: basePrice is per-bundle unit, multiply by bundle quantity
        return resolvedBasePrice * bundleQuantity;
      }
      const baseOfferBundlePrice = product.productBaseOfferBundlePrice;
      if (baseOfferBundlePrice != null) {
        return baseOfferBundlePrice * bundleQuantity;
      }
      return resolvedBasePrice * bundleQuantity * (bundleFactor ?? 1);
    }
    return resolvedBasePrice * adjustedQuantity;
  }, [
    isTcpPricing,
    resolvedBasePrice,
    product.productBaseOfferBundlePrice,
    entitlement,
    showBundles,
    bundleQuantity,
    bundleFactor,
    adjustedQuantity,
  ]);

  // Signatory discount = creditsTotal - basePriceTotal (negative = savings)
  const signatoryDiscount = useMemo(() => {
    if (basePriceTotal == null || creditsTotal == null) return undefined;
    return creditsTotal - basePriceTotal;
  }, [basePriceTotal, creditsTotal]);

  // Estimated taxes based on the discounted price (creditsTotal)
  const estimatedTaxes = useMemo(
    () => (creditsTotal !== undefined ? creditsTotal * (taxRate / 100) : undefined),
    [creditsTotal, taxRate]
  );

  // Order total: creditsTotal + estimatedTaxes
  const orderTotal = useMemo(() => {
    if (creditsTotal === undefined || estimatedTaxes === undefined) return undefined;
    return creditsTotal + estimatedTaxes;
  }, [creditsTotal, estimatedTaxes]);

  // ─── Adjustment range (step 1 only) ──────────────────────────────────────────

  const { minimumAdjustment, maximumAdjustment } = useMemo(() => {
    if (showBundles && bundleQuantity > 0 && bundleFactor) {
      const minBundles = Math.max(Math.floor(bundleQuantity * (1 - ADJUSTMENT_FACTOR)), 1);
      const maxBundles = Math.ceil(bundleQuantity * (1 + ADJUSTMENT_FACTOR));

      return {
        minimumAdjustment: minBundles * bundleFactor,
        maximumAdjustment: Math.min(maxBundles * bundleFactor, creditLimit),
      };
    }
    const minCredits = Math.max(Math.floor(adjustedQuantity * (1 - ADJUSTMENT_FACTOR)), 1);
    const maxCredits = Math.min(Math.ceil(adjustedQuantity * (1 + ADJUSTMENT_FACTOR)), creditLimit);

    return {
      minimumAdjustment: minCredits,
      maximumAdjustment: maxCredits,
    };
  }, [showBundles, bundleQuantity, bundleFactor, adjustedQuantity, creditLimit]);

  // ─── Formatted display values ────────────────────────────────────────────────

  const adjustmentValue = useMemo(() => {
    if (minimumAdjustment === undefined || maximumAdjustment === undefined) return undefined;
    const minFormatted = Math.floor(minimumAdjustment).toLocaleString('en-US');
    const maxFormatted = Math.ceil(maximumAdjustment).toLocaleString('en-US');
    return `${minFormatted} – ${maxFormatted}`;
  }, [minimumAdjustment, maximumAdjustment]);

  const creditsTotalValue = useMemo(
    () => (creditsTotal !== undefined ? formatCurrency(creditsTotal, currencyCode) : undefined),
    [creditsTotal, currencyCode]
  );

  const basePriceTotalValue = useMemo(
    () => (basePriceTotal !== undefined ? formatCurrency(basePriceTotal, currencyCode) : undefined),
    [basePriceTotal, currencyCode]
  );

  const signatoryDiscountValue = useMemo(
    () => (signatoryDiscount !== undefined ? formatCurrency(signatoryDiscount, currencyCode) : undefined),
    [signatoryDiscount, currencyCode]
  );

  const estimatedTaxesValue = useMemo(
    () => (estimatedTaxes !== undefined ? formatCurrency(estimatedTaxes, currencyCode) : undefined),
    [estimatedTaxes, currencyCode]
  );

  const orderTotalValue = useMemo(
    () => (orderTotal !== undefined ? formatCurrency(orderTotal, currencyCode) : undefined),
    [orderTotal, currencyCode]
  );

  // ─── Component state ─────────────────────────────────────────────────────────

  const state = useMemo(
    () =>
      adjustmentValue !== undefined &&
      orderTotalValue !== undefined &&
      creditsTotalValue !== undefined &&
      estimatedTaxesValue !== undefined
        ? COMPUTED_INPUT_STATE.READY
        : COMPUTED_INPUT_STATE.LOADING,
    [adjustmentValue, orderTotalValue, creditsTotalValue, estimatedTaxesValue]
  );

  // ─── Build order values ──────────────────────────────────────────────────────

  const orderValues: ComputedInputValue[] = [];

  // Delivery date (steps 2 & 3)
  if (deliveryDate) {
    orderValues.push({
      label: t(checkoutBundle, 'checkout_step_2_delivery'),
      value: deliveryDate,
      calculatingLabel: t(commonBundle, CALCULATING_INPUT_LABEL),
    });
  }

  // Bundle/credit quantities
  if (showBundles) {
    const totalCredits = bundleQuantity * (bundleFactor ?? 1);
    const batchLabel =
      variant === 'confirmed'
        ? t(checkoutBundle, 'checkout_step_3_batch_quantity_confirmed_label')
        : t(checkoutBundle, 'checkout_step_1_second_card_order_total_label_1');
    const creditLabel =
      variant === 'confirmed'
        ? t(checkoutBundle, 'checkout_step_3_highlightedBanner_1_title')
        : t(checkoutBundle, 'checkout_step_1_second_card_order_total_label_2');

    orderValues.push(
      {
        label: batchLabel,
        value: bundleQuantity.toString(),
        calculatingLabel: t(commonBundle, CALCULATING_INPUT_LABEL),
      },
      {
        label: creditLabel,
        value: totalCredits.toString(),
        calculatingLabel: t(commonBundle, CALCULATING_INPUT_LABEL),
      }
    );
  } else if (deliveryDate) {
    // Non-bundle products on steps 2 & 3: show credit quantity
    const creditLabel =
      variant === 'confirmed'
        ? t(checkoutBundle, 'checkout_step_3_highlightedBanner_1_title')
        : t(checkoutBundle, 'checkout_step_1_second_card_order_total_label_2');

    orderValues.push({
      label: creditLabel,
      value: adjustedQuantity.toString(),
      calculatingLabel: t(commonBundle, CALCULATING_INPUT_LABEL),
    });
  }

  // Subtotal: show base price when TCP so the discount + total math adds up
  const displayedCreditsTotal = isTcpPricing && basePriceTotalValue ? basePriceTotalValue : creditsTotalValue;

  orderValues.push({
    label: t(checkoutBundle, 'checkout_step_1_second_card_subtotal_label'),
    value: displayedCreditsTotal?.toString() || '0',
    calculatingLabel: t(commonBundle, CALCULATING_INPUT_LABEL),
  });

  // Signatory discount line (only when TCP pricing applies)
  if (isTcpPricing && signatoryDiscountValue) {
    orderValues.push({
      label: t(checkoutBundle, 'checkout_step_1_second_card_signatory_discount_label'),
      value: signatoryDiscountValue.toString(),
      calculatingLabel: t(commonBundle, CALCULATING_INPUT_LABEL),
    });
  }

  // Estimated taxes
  const estimatedTaxesLabel =
    taxRate > 0
      ? t(checkoutBundle, 'checkout_step_1_second_card_estimated_taxes_vat_label', { taxType, taxRate })
      : t(checkoutBundle, 'checkout_step_1_second_card_estimated_taxes_label');

  orderValues.push(
    {
      label: estimatedTaxesLabel,
      value: estimatedTaxesValue?.toString() || '0',
      calculatingLabel: t(commonBundle, CALCULATING_INPUT_LABEL),
    },
    {
      label: t(checkoutBundle, 'checkout_step_1_second_card_order_total_label_3'),
      value: orderTotalValue?.toString() || '0',
      calculatingLabel: t(commonBundle, CALCULATING_INPUT_LABEL),
      isBold: true,
    }
  );

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className={styles.creditsComputedInfo}>
      <ComputedInput
        state={state}
        title={
          showBundles && showBundleNote ? t(checkoutBundle, 'checkout_step_1_second_card_order_total_title') : undefined
        }
        values={orderValues}
      />
      {showAllowedAdjustments && (
        <>
          <p className={styles.copy}>{t(checkoutBundle, 'checkout_step_1_second_card_allowed_adjustments_title')}</p>
          <ComputedInput
            state={state}
            values={[
              {
                label: t(checkoutBundle, 'checkout_step_1_second_card_allowed_adjustments_label_1'),
                value: adjustmentValue?.toString() || '0',
                calculatingLabel: t(commonBundle, CALCULATING_INPUT_LABEL),
              },
            ]}
          />
        </>
      )}
    </div>
  );
};
